import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "#app/utils/misc";

export type HeadingItem = { id: string; title: string; level: number };

export function OnThisPage({
  headings,
  className,
}: {
  headings: HeadingItem[];
  className?: string;
}) {
  const [activeId, setActiveId] = useState<string>("");
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!headings || headings.length === 0) return;

    // Use the main doc scroll container as root so "On this page" highlights when scrolling the doc content
    const scrollRoot = document.querySelector("[data-docs-scroll-root]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        root: scrollRoot ?? undefined,
        rootMargin: "-80px 0px -66% 0px",
        threshold: 0,
      },
    );

    // Observe all headings (they live inside the main scroll container)
    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [headings]);

  if (!headings || headings.length === 0) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      // Get the main content area
      const mainContent = element.closest("main");
      if (mainContent) {
        // Calculate position relative to the scrollable container
        const elementTop = element.offsetTop;
        mainContent.scrollTo({
          top: elementTop - 100,
          behavior: "smooth",
        });
      } else {
        // Fallback to window scroll
        const top =
          element.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top, behavior: "smooth" });
      }
      setActiveId(id);
    }
  };

  return (
    <div className={cn("space-y-1", className)}>
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center justify-between w-full text-xs font-semibold text-gray-500 dark:text-zinc-400 uppercase tracking-wider mb-3 hover:text-gray-700 transition-colors dark:hover:text-gray-300"
      >
        <span className="flex items-center gap-2">
          <span>📄</span>
          On this page
        </span>
        {isCollapsed ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronUp className="h-3 w-3" />
        )}
      </button>

      {!isCollapsed && (
        <nav className="space-y-1">
          {headings.map((h) => {
            const isActive = activeId === h.id;

            return (
              <a
                key={h.id}
                href={`#${h.id}`}
                onClick={(e) => handleClick(e, h.id)}
                className={cn(
                  "block py-1.5 text-sm transition-all rounded-md px-2",
                  h.level === 2 && "pl-2",
                  h.level === 3 && "pl-5 text-[13px]",
                  h.level >= 4 && "pl-8 text-[13px]",
                  isActive
                    ? "relative text-[#535EA4] font-medium bg-[linear-gradient(90deg,#F0F4FF_0%,#FFFFFF_100%)] before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:rounded-full before:bg-[linear-gradient(180deg,#535EA4_0%,#909CCA_100%)] dark:bg-none dark:bg-zinc-800 dark:text-indigo-300"
                    : "text-gray-600 hover:text-[#141A42] hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-zinc-800",
                )}
                title={h.title}
              >
                <span className="line-clamp-2">{h.title}</span>
              </a>
            );
          })}
        </nav>
      )}
    </div>
  );
}
